import { useState } from 'react';
import { Facebook, Instagram, Linkedin, Twitter, Youtube, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { NAV, CATEGORIES } from '../../data/content';
import { subscribeNewsletter } from '../../lib/api';
import { useSiteData } from '../../context/SiteDataContext';
import { useTranslation } from 'react-i18next';

const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

const MENU_ITEMS = [
  { key: 'nav.home', label: 'Home', to: '/' },
  { key: 'nav.specialties', label: 'Products', to: '/products' },
  { key: 'nav.quality', label: 'Quality & Manufacturing', to: '/quality-and-manufacturing' },
  { key: 'nav.downloads', label: 'Downloads', to: '/downloads' },
  { key: 'nav.faqs', label: 'FAQs', to: '/faqs' },
  { key: 'nav.about_us', label: 'About Us', to: '/about-us' },
  { key: 'nav.contact', label: 'Contact', to: '/contact' },
];

const Footer = () => {
  const { t } = useTranslation();
  const { settings } = useSiteData();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return toast.error(t('footer.email_placeholder'));
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      toast.success(t('footer.newsletter_desc'));
      setEmail('');
    } catch {
      toast.error('Subscription failed. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const footerAbout = settings.footer_about || t('footer.about');
  const socialLinks = settings.social_links || {};

  const SOCIAL_DEFS = [
    { key: 'facebook', label: 'Facebook', Icon: Facebook },
    { key: 'instagram', label: 'Instagram', Icon: Instagram },
    { key: 'linkedin', label: 'LinkedIn', Icon: Linkedin },
    { key: 'twitter', label: 'X (Twitter)', Icon: Twitter },
    { key: 'youtube', label: 'YouTube', Icon: Youtube },
    { key: 'whatsapp', label: 'WhatsApp', Icon: WhatsAppIcon },
    { key: 'tiktok', label: 'TikTok', Icon: TikTokIcon },
  ];

  const socials = SOCIAL_DEFS.map(({ key, label, Icon }) => ({
    key,
    label,
    Icon,
    url: socialLinks[key],
  })).filter((s) => s.url && s.url.trim());

  return (
    <footer data-testid="site-footer" className="bg-mt-footer pt-20 pb-10 text-white">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <img src="/logo-white.png" alt="MEDTREND" className="h-8 w-auto" />
            <p className="mt-6 max-w-xs leading-relaxed text-slate-400">
              {footerAbout}
            </p>
            {socials.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-3">
                {socials.map((s) => (
                  <a
                    key={s.key}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`social-${s.key}`}
                    aria-label={s.label}
                    className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-slate-300 transition-colors duration-300 hover:bg-mt-sky hover:text-white"
                  >
                    <s.Icon className="h-4.5 w-4.5" strokeWidth={1.5} />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div>
            <h4 className="font-display font-bold text-white">{t('footer.quick_links')}</h4>
            <ul className="mt-5 space-y-3">
              {MENU_ITEMS.map((n) => (
                <li key={n.key}>
                  <Link to={n.to} className="text-slate-400 transition-colors hover:text-mt-sky">
                    {t(n.key)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display font-bold text-white">{t('footer.products')}</h4>
            <ul className="mt-5 space-y-3">
              {CATEGORIES.slice(0, 6).map((c) => (
                <li key={c.name}>
                  <Link to={`/category/${c.name.toLowerCase().replace(/\s+/g, '-')}`} className="text-slate-400 transition-colors hover:text-mt-sky">
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display font-bold text-white">{t('footer.newsletter')}</h4>
            <p className="mt-5 text-slate-400">{t('footer.newsletter_desc')}</p>
            <form onSubmit={subscribe} className="mt-4 flex overflow-hidden rounded-full bg-white/5 p-1.5 border border-white/10">
              <input
                data-testid="newsletter-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('footer.email_placeholder')}
                className="w-full bg-transparent px-4 text-sm text-white placeholder:text-slate-500 focus:outline-none"
              />
              <button
                data-testid="newsletter-submit"
                type="submit"
                disabled={loading}
                className="flex h-10 w-11 shrink-0 items-center justify-center rounded-full bg-mt-orange text-white transition-colors hover:bg-mt-orangeLight disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" strokeWidth={2} />}
              </button>
            </form>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 text-sm text-slate-500 md:flex-row">
          <p>© {new Date().getFullYear()} MEDTREND. {t('footer.copyright')}</p>
          <div className="flex gap-6">
            <Link to="/privacy-policy" className="transition-colors hover:text-mt-sky">{t('footer.privacy')}</Link>
            <Link to="/terms-of-service" className="transition-colors hover:text-mt-sky">{t('footer.terms')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
